/**
 * The scoring screen. One screen, no navigation inside the loop.
 *
 * The whole surface is two rows: a read half on top (header, over strip, scorecard) and a
 * tap half below (the pad, or whatever the prompt gate is asking for). Nothing here opens
 * a route, a page or a full-screen modal — the bottom half swaps its keys, and the score
 * stays visible the entire time.
 *
 * There is no loading state, no spinner and nothing async on any path. Every tap appends
 * to an array, re-folds it and re-renders synchronously: a whole innings is ~250 events
 * and single-digit milliseconds, so the 100ms budget in PRD §6.8 is met structurally
 * rather than optimised towards. When #13 puts a real outbox behind this, the write still
 * lands locally first and the network never sits between a tap and the pixels (ADR-0010).
 */
import { useEffect, useMemo, useState } from "react";
import { battingHandName, bowlingStyleName, formatOvers } from "@sportsos/engine";
import type {
  BatterHand,
  BowlingArm,
  BowlingType,
  Event as LedgerEvent,
  EventId,
  NewOver,
  PlayerId,
  Shot,
} from "@sportsos/engine";
import type { Latch, LatchKey } from "../match/entry.ts";
import { NO_LATCH, toggleLatch } from "../match/entry.ts";
import type { MatchDraft } from "../match/draft.ts";
import type { SquadPlayer } from "../match/setup.ts";
import { api, ApiRequestError } from "../api.ts";
import {
  bowlerWarnings,
  buildBatterIn,
  buildBowlerChange,
  buildDelivery,
  buildNewOver,
  buildRetired,
  buildSecondInnings,
  buildSecondSuperOverInnings,
  buildSuperOverInnings,
  canStepBack,
  canSwapIncomingBatters,
  canUndoLast,
  changeOpener,
  crease,
  derive,
  liveEvents,
  nextPrompt,
  picksBeforeFirstBall,
  stepBack,
  swapIncomingBatters,
  undoLast,
} from "../match/draft.ts";
import type { OpenerSlot } from "../match/draft.ts";
import { OpenersStrip } from "./OpenersStrip.tsx";
import type { Ledger } from "../match/useLedger.ts";
import { formatScoringReport, scoringObserver } from "../match/scoringSession.ts";
import { localStore } from "../localStore.ts";
import { ForeignBanner, GapBanner, StuckBanner, SyncPill } from "./SyncPill.tsx";
import { nameLookup, teamById, xi } from "../match/setup.ts";
import { handChangeImpact, knownBattingHand, knownBowling } from "../match/styles.ts";
import { asksForDirection } from "../match/wagonEntry.ts";
import { setWagonMode, toggleWagonMode, wagonMode, type WagonMode } from "../match/wagonPrefs.ts";
import { latchLabel } from "../match/entry.ts";
import { Header } from "./Header.tsx";
import { LedgerView, OverStrip } from "./OverStrip.tsx";
import { EditBallSheet, type BallCorrection } from "./EditBallSheet.tsx";
import {
  correctBattingHand,
  correctBowlingStyle,
  editBall,
  findDelivery,
  reassignOver,
  redirectBall,
  substitutePlayer,
  swapEndsFrom,
  undoLastBalls,
  undoableBalls,
  voidBall,
} from "../match/corrections.ts";
import { Pad } from "./Pad.tsx";
import { BatterPrompt, BowlerPrompt, InningsBreak, MatchOver, RetireSheet, SuperOverPrompt, SuperOverTiedPrompt } from "./Prompts.tsx";
import { Scorecard } from "./Scorecard.tsx";
import { ConfirmChange, HandStep } from "./StyleSteps.tsx";
import { WagonWheelSheet } from "./WagonWheelSheet.tsx";
import { WicketSheet } from "./WicketSheet.tsx";
import type { WicketResult } from "./WicketSheet.tsx";

/** What the bottom half is showing, when it is not showing the pad. */
type Sheet =
  | { readonly kind: "wicket"; readonly runs: number }
  | { readonly kind: "retire" }
  | { readonly kind: "bowler_change" }
  | { readonly kind: "keeper_change" }
  /** Putting someone else in one opening slot, from the Openers strip, before the first ball. */
  | { readonly kind: "change_opener"; readonly slot: OpenerSlot }
  /** Correcting one historical ball, reached by tapping its chip (#15). */
  | { readonly kind: "edit_ball"; readonly eventId: EventId }
  /** Every ball of the innings, for reaching one the current-over strip cannot. */
  | { readonly kind: "ledger" }
  /**
   * Where a scoring shot went, asked before the ball is written (ADR-0022). The ball is held here,
   * as `runs` plus the latch it was tapped with, and becomes one delivery on place or skip.
   */
  | { readonly kind: "wagon"; readonly runs: number; readonly latch: Latch }
  /** Saying where an already-recorded ball went, from the edit sheet. */
  | { readonly kind: "wagon_edit"; readonly eventId: EventId }
  /**
   * A hand or arm correction waiting on the scorer's yes, because it reaches back over balls already
   * scored (ADR-0023). `keep` is where Keep returns to, and `done` where the sheet goes once it is applied.
   */
  | { readonly kind: "confirm_change"; readonly change: StyleChange; readonly keep: Sheet | null; readonly done: Sheet | null };

/** A change to how someone bats or bowls, made while the match is under way (ADR-0023). */
type StyleChange =
  | { readonly kind: "hand"; readonly playerId: PlayerId; readonly hand: BatterHand }
  | {
      readonly kind: "bowling";
      readonly bowlerId: PlayerId;
      readonly overNo: number;
      readonly arm?: BowlingArm;
      readonly type?: BowlingType;
    };

interface Props {
  readonly ledger: Ledger;
  readonly dark: boolean;
  readonly onToggleTheme: () => void;
  readonly onExit: () => void;
  readonly onMatchCompleted: () => void;
  /** Shown on the pill when the session has lapsed. Absent where there is nowhere to go. */
  readonly onSignIn?: () => void;
}

export function ScoringScreen({
  ledger,
  dark,
  onToggleTheme,
  onExit,
  onMatchCompleted,
  onSignIn,
}: Props): React.ReactElement {
  const { draft, record } = ledger;
  const [latch, setLatch] = useState<Latch>(NO_LATCH);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [recentFielderId, setRecentFielderId] = useState<PlayerId | null>(null);
  const [completing, setCompleting] = useState(false);
  const [completionError, setCompletionError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  // Per match and per device, and off until the scorer turns it on (ADR-0022).
  const [wagon, setWagon] = useState<WagonMode>(() => wagonMode(localStore(), String(draft.setup.matchId)));

  // Keyed on the event array's identity, which changes on exactly the appends and undos
  // that can change the state — so a re-render for an unrelated reason does not re-fold.
  const state = useMemo(() => derive(draft), [draft]);
  const name = useMemo(() => nameLookup(draft.setup), [draft.setup]);

  /**
   * The scoring-loop funnel (PRD §13, #16).
   *
   * Per match, so one match cannot overwrite another's evidence, and created once for the life
   * of the screen. Every call below is a write to `localStorage` and nothing else — no network,
   * no vendor (PRD FN-3) — and every one of them swallows its own failures, so nothing here can
   * cost a ball.
   */
  const loop = useMemo(
    () => scoringObserver(localStore(), String(draft.setup.matchId), () => Date.now()),
    [draft.setup.matchId],
  );

  // The pill is the only signal for "a ball is on the device and not on the server", and how
  // long it showed one is a named observation of the field test. Watched here rather than
  // inside `useLedger` because this is where the pill is rendered — the measurement and the
  // thing measured stay together.
  useEffect(() => {
    loop.sync(ledger.sync);
  }, [loop, ledger.sync]);

  const innings = state.innings[state.innings.length - 1];
  const prompt = nextPrompt(draft, state);
  const at = crease(draft, state);

  // A held ball must not outlive the state it was tapped against. If a prompt appears under the
  // wheel (an over closed by another device, say), the held ball would otherwise come back after
  // the prompt was answered, carrying runs for a ball the screen no longer expects.
  const holdingBall = sheet?.kind === "wagon";
  useEffect(() => {
    if (holdingBall && prompt !== null) setSheet(null);
  }, [holdingBall, prompt]);

  /**
   * Record one event and reset the pad.
   *
   * The latch and the sheet are cleared *before* the write resolves, on purpose: the scorer
   * needs the pad ready for the next ball immediately, and the ball itself is already safe —
   * `record` does not resolve until it is on disk. Nothing derived from the ledger updates
   * early, because the draft only changes once the write lands.
   */
  const advance = (
    build: (current: MatchDraft) => LedgerEvent | readonly LedgerEvent[] | null,
  ): void => {
    setLatch(NO_LATCH);
    setSheet(null);
    void record(build);
  };

  const teamName = (teamId: Parameters<typeof teamById>[1]): string =>
    teamById(draft.setup, teamId).name;

  if (innings === undefined) return <div className="app-screen__read">No innings yet</div>;

  const battingTeam = teamById(draft.setup, innings.battingTeamId);
  const bowlingTeam = teamById(draft.setup, innings.bowlingTeamId);

  /**
   * A run digit is the commit — unless `W` is latched, in which case it opens the wicket
   * sheet carrying the runs with it. That is what keeps the ordinary ball at one tap while
   * still letting `NB → W → 2` describe a run out off a no-ball for two.
   */
  const onRuns = (runs: number): void => {
    if (at === null) return;
    loop.touched();
    if (latch.wicket) {
      setSheet({ kind: "wicket", runs });
      return;
    }
    if (asksForDirection(latch, runs, wagon)) {
      // The ball is not written yet. It is held in the sheet and becomes one delivery on place or
      // skip (ADR-0022), so a scorer who taps Back has recorded nothing to undo.
      setSheet({ kind: "wagon", runs, latch });
      return;
    }
    loop.ball();
    advance((current) => buildDelivery(current, state, at, latch, runs, null));
  };

  /** The wheel answered, or was skipped. Either way exactly one ball is written. */
  const onWagonDone = (held: { runs: number; latch: Latch }, shot: Shot | null): void => {
    if (at === null) return;
    loop.wagon(shot === null ? "skipped" : "placed");
    loop.ball();
    advance((current) => buildDelivery(current, state, at, held.latch, held.runs, null, shot));
  };

  /** Which way a batter stands this innings, as the ledger has it. Null when it was never recorded. */
  const handOfBatter = (playerId: PlayerId): BatterHand | null =>
    innings.batting.find((line) => line.playerId === playerId)?.hand ?? null;

  const squadMember = (playerId: PlayerId): SquadPlayer | undefined =>
    [...battingTeam.players, ...bowlingTeam.players].find((player) => player.id === playerId);

  /** Write a hand or arm correction, then go to `done`. Through `record`, so it is durable first. */
  const applyStyleChange = (change: StyleChange, done: Sheet | null): void => {
    void record((current) =>
      change.kind === "hand"
        ? correctBattingHand(current, innings.inningsNo, change.playerId, change.hand)
        : correctBowlingStyle(current, innings.inningsNo, change.overNo, change.bowlerId, {
            ...(change.arm === undefined ? {} : { arm: change.arm }),
            ...(change.type === undefined ? {} : { type: change.type }),
          }),
    ).then(() => setSheet(done));
  };

  /**
   * Ask before a correction that reaches into balls already scored (ADR-0023).
   *
   * A hand change is not "from the next ball": it re-reads every ball the batter has faced and
   * redraws every shot he placed. A different arm is the same for every over the bowler has
   * bowled. Both say so, in numbers, and the safe answer is first. When nothing has been scored
   * against the person, or the value was never recorded, there is nothing to rewrite and it is
   * applied at once. A change of type alone touches one over and is never warned about.
   */
  const requestStyleChange = (change: StyleChange, keep: Sheet | null, done: Sheet | null): void => {
    if (change.kind === "hand") {
      const current = handOfBatter(change.playerId);
      if (current === change.hand) {
        setSheet(keep);
        return;
      }
      const impact = handChangeImpact(state, innings.inningsNo, change.playerId);
      if (current === null || impact.balls === 0) applyStyleChange(change, done);
      else setSheet({ kind: "confirm_change", change, keep, done });
      return;
    }
    const known = innings.bowling.find((line) => line.playerId === change.bowlerId);
    const armChanges = change.arm !== undefined && known?.arm != null && known.arm !== change.arm;
    if (armChanges && known !== undefined && (known.overs.completed > 0 || known.overs.balls > 0)) {
      setSheet({ kind: "confirm_change", change, keep, done });
      return;
    }
    applyStyleChange(change, done);
  };

  // Available for the whole match, so a scorer who forgot can start it in the third over.
  const toggleWagon = (): void => {
    const next = toggleWagonMode(wagon);
    setWagon(next);
    setWagonMode(localStore(), String(draft.setup.matchId), next);
  };

  const onWicketConfirmed = ({ wicket, runs }: WicketResult): void => {
    if (at === null) return;
    const fielder = wicket.fielderIds[0];
    if (fielder !== undefined) setRecentFielderId(fielder);
    loop.ball();
    // The `W` latch comes off with the rest, so the next ball starts clean.
    advance((current) => buildDelivery(current, state, at, { ...latch, wicket: false }, runs, wicket));
  };

  // ── The bottom half ───────────────────────────────────────────────────────

  /**
   * Turn one decision from the edit sheet into events.
   *
   * Through `advance`, so a correction takes the same durable-before-visible path as a ball:
   * written to disk in order, then folded, then rendered. A correction that appeared on screen
   * before it was stored would be one a crash could lose.
   */
  const onCorrect = (eventId: EventId, correction: BallCorrection): void => {
    // How someone bats and bowls is not a mistake on the pad, so it is not counted against it, and
    // it may need the scorer's yes first (ADR-0023).
    if (correction.kind === "batting_hand") {
      requestStyleChange(
        { kind: "hand", playerId: correction.playerId, hand: correction.hand },
        { kind: "edit_ball", eventId },
        null,
      );
      return;
    }
    if (correction.kind === "bowling_style") {
      requestStyleChange(
        {
          kind: "bowling",
          bowlerId: correction.bowlerId,
          overNo: correction.overNo,
          ...(correction.arm === undefined ? {} : { arm: correction.arm }),
          ...(correction.type === undefined ? {} : { type: correction.type }),
        },
        { kind: "edit_ball", eventId },
        null,
      );
      return;
    }
    // Corrections per match is the proxy for entry ergonomics (PRD §13): a pad that is hard to
    // hit accurately shows up here long before a scorer thinks to complain about it.
    loop.corrected(correction.kind);
    advance((current) => {
      switch (correction.kind) {
        case "runs":
          return editBall(current, eventId, {
            latch: correction.latch,
            runs: correction.runs,
            // The wicket is carried across untouched. Changing a dismissal is its own flow —
            // it needs the fielder and the end, which is the whole of `WicketSheet` — and
            // silently dropping one because the runs were edited would lose a wicket.
            wicket: findDelivery(current, eventId)?.wicket ?? null,
          });
        case "void":
          return voidBall(current, eventId);
        case "swap_ends":
          return swapEndsFrom(current, eventId);
        case "reassign_over": {
          const state_ = derive(current);
          const ball = state_.innings
            .find((entry) => entry.inningsNo === innings.inningsNo)
            ?.deliveries.find((chip) => chip.eventId === eventId);
          return ball === undefined
            ? null
            : reassignOver(current, state_, innings.inningsNo, ball.over, correction.bowlerId);
        }
        case "substitute": {
          const stored = findDelivery(current, eventId);
          return stored === undefined
            ? null
            : substitutePlayer(current, innings.inningsNo, {
                fromPlayerId: correction.fromPlayerId,
                toPlayerId: correction.toPlayerId,
                chosenSeq: stored.seq,
                reason: "misidentified player",
              });
        }
      }
    });
  };

  /** "← Back" on the opening questions: takes back only the pick before, while there is one. */
  const stepBackButton = canStepBack(draft, state)
    ? { label: "← Back", onClick: () => advance((current) => stepBack(current, derive(current))) }
    : undefined;

  // The Openers strip: only before the first ball of an innings.
  const openersStrip =
    picksBeforeFirstBall(draft, state).length > 0 && state.header !== null ? (
      <OpenersStrip
        names={{
          striker: state.header.striker === null ? null : name(state.header.striker.playerId),
          nonStriker: state.header.nonStriker === null ? null : name(state.header.nonStriker.playerId),
          bowler: state.header.bowler === null ? null : name(state.header.bowler.playerId),
        }}
        onChange={(slot) => setSheet({ kind: "change_opener", slot })}
      />
    ) : null;

  const bottom = ((): React.ReactElement => {
    // ── Corrections outrank the prompt gate ─────────────────────────────────
    // Deliberately first, and it took driving the app to see why. The gate asks for the next
    // bowler the moment an over closes — which is exactly when a scorer realises the over was
    // recorded against the wrong one. Below the gate, the headline correction in the product
    // would be unreachable until he had first named a bowler for an over he had not yet
    // decided to keep. A correction is how a wrong state gets fixed, so it cannot queue
    // behind a question that wrong state produced.
    if (sheet?.kind === "ledger") {
      return (
        <div className="stack">
          <div>
            <div className="ds-title">Every ball this innings</div>
            <div className="ds-caption">
              Tap any of them to correct it. Newest over first.
            </div>
          </div>
          <LedgerView
            balls={innings.deliveries}
            onTapBall={(eventId) => setSheet({ kind: "edit_ball", eventId })}
          />
          <button className="ds-btn ds-btn--secondary ds-btn--block" onClick={() => setSheet(null)}>Close</button>
        </div>
      );
    }

    if (sheet?.kind === "edit_ball") {
      const ball = innings.deliveries.find((chip) => chip.eventId === sheet.eventId);
      const stored = findDelivery(draft, sheet.eventId);
      // Both gone means the ball was voided while the sheet was open — by an undo, or by
      // another device. Closing is the honest response; a sheet editing a ball that no longer
      // exists would build a correction that supersedes nothing.
      if (ball === undefined || stored === undefined) {
        return (
          <div className="stack">
            <div className="ds-title">That ball is no longer there</div>
            <div className="ds-caption">It was undone or voided. Nothing was changed.</div>
            <button className="ds-btn ds-btn--secondary ds-btn--block" onClick={() => setSheet(null)}>Close</button>
          </div>
        );
      }
      const person = (playerId: PlayerId): SquadPlayer | undefined =>
        [...battingTeam.players, ...bowlingTeam.players].find((p) => p.id === playerId);

      return (
        <EditBallSheet
          ball={ball}
          striker={person(stored.strikerId)}
          nonStriker={person(stored.nonStrikerId)}
          bowler={person(stored.bowlerId)}
          hasWicket={stored.wicket !== null}
          bowlers={xi(bowlingTeam)}
          squad={[...battingTeam.players, ...bowlingTeam.players]}
          onClose={() => setSheet(null)}
          onCorrect={(correction) => onCorrect(sheet.eventId, correction)}
          shot={ball.shot}
          canDirect={wagon !== "off" || ball.shot !== null}
          onDirection={() => setSheet({ kind: "wagon_edit", eventId: sheet.eventId })}
          overStyle={((): { arm: BowlingArm | null; type: BowlingType | null } => {
            const started = liveEvents(draft).find(
              (event): event is NewOver =>
                event.type === "NewOver" && event.inningsNo === innings.inningsNo && event.overNo === ball.over,
            );
            return { arm: started?.bowlingArm ?? null, type: started?.bowlingType ?? null };
          })()}
        />
      );
    }

    if (sheet?.kind === "wagon_edit") {
      const ball = innings.deliveries.find((chip) => chip.eventId === sheet.eventId);
      if (ball === undefined) {
        return (
          <div className="stack">
            <div className="ds-title">That ball is no longer there</div>
            <button className="ds-btn ds-btn--secondary ds-btn--block" onClick={() => setSheet(null)}>Close</button>
          </div>
        );
      }
      const hand = ball.batterHand;
      // A shot cannot be placed on a ground whose off side is not known, so the hand is asked first.
      if (hand === null) {
        return (
          <HandStep
            name={name(ball.batterId)}
            current={null}
            onPick={(next) => applyStyleChange({ kind: "hand", playerId: ball.batterId, hand: next }, sheet)}
            onBack={() => setSheet({ kind: "edit_ball", eventId: sheet.eventId })}
          />
        );
      }
      return (
        <WagonWheelSheet
          runs={ball.runs.batter}
          batterName={name(ball.batterId)}
          hand={hand}
          latchLabel=""
          editing
          initial={ball.shot}
          onHand={(next) => requestStyleChange({ kind: "hand", playerId: ball.batterId, hand: next }, sheet, sheet)}
          onBack={() => setSheet({ kind: "edit_ball", eventId: sheet.eventId })}
          onPlace={(shot) => {
            loop.corrected("direction");
            advance((current) => redirectBall(current, sheet.eventId, shot));
          }}
          onSkip={() => {
            if (ball.shot === null) {
              setSheet({ kind: "edit_ball", eventId: sheet.eventId });
              return;
            }
            loop.corrected("direction");
            advance((current) => redirectBall(current, sheet.eventId, null));
          }}
        />
      );
    }


    if (prompt?.kind === "match_over") {
      const winner = state.result?.winnerTeamId;
      const report = loop.report();
      return (
        <MatchOver
          state={state}
          winnerName={winner == null ? null : teamName(winner)}
          loopReport={report === null ? null : formatScoringReport(report)}
          completing={completing}
          completionError={completionError}
          completed={completed}
          onComplete={async () => {
            if (completing || completed) return;
            setCompleting(true);
            setCompletionError(null);
            try {
              await api.completeMatch(String(draft.setup.matchId));
              setCompleted(true);
              onMatchCompleted();
            } catch (error: unknown) {
              const message =
                error instanceof ApiRequestError
                  ? error.body.message
                  : "Could not finalise. Check your connection and try again.";
              setCompletionError(message);
            } finally {
              setCompleting(false);
            }
          }}
        />
      );
    }

    if (prompt?.kind === "super_over") {
      // The last completed pair is level. Show the tied runs.
      const tiedRuns = state.innings[state.innings.length - 1]!.runs;
      return (
        <SuperOverPrompt
          runs={tiedRuns}
          onStart={() => advance((current) => buildSuperOverInnings(current, state))}
        />
      );
    }

    if (prompt?.kind === "super_over_tied") {
      return (
        <SuperOverTiedPrompt
          onAnother={() => advance((current) => buildSuperOverInnings(current, state))}
          onDeclareTied={() => {
            // Do nothing to the ledger. The scorer can use "Finalise result" which will
            // record the tie via the match completion API. Closing this prompt by
            // completing the match is the intended path.
          }}
        />
      );
    }

    if (prompt?.kind === "innings_break") {
      // When a super over's first innings ends, the break starts the chase of that
      // super over pair — not a normal second innings.
      const isSuperOverBreak = innings.isSuperOver;
      return (
        <InningsBreak
          innings={innings}
          teamName={battingTeam.name}
          chasingName={bowlingTeam.name}
          onStart={() =>
            advance((current) =>
              isSuperOverBreak
                ? buildSecondSuperOverInnings(current, state)
                : buildSecondInnings(current, state),
            )
          }
        />
      );
    }

    if (prompt?.kind === "batter") {
      const atCrease = new Set<string>(
        [state.header?.striker?.playerId, state.header?.nonStriker?.playerId].filter(
          (id): id is PlayerId => id !== undefined && id !== null,
        ),
      );
      // Out is out, and retired out cannot return. Retired hurt can, and a fall of wicket
      // is exactly when — so those stay on the list.
      const unavailable = new Set<string>(
        innings.batting
          .filter((line) => line.dismissal !== null || line.retired === "out")
          .map((line) => line.playerId),
      );
      const available = xi(battingTeam).filter(
        (player) => !atCrease.has(player.id) && !unavailable.has(player.id),
      );
      return (
        <BatterPrompt
          takesStrike={prompt.takesStrike}
          available={available}
          innings={innings}
          handOf={(playerId) => knownBattingHand(draft.setup, state, playerId)}
          onPick={(playerId, hand) =>
            advance((current) =>
              buildBatterIn(current, innings.inningsNo, playerId, prompt.takesStrike, hand),
            )
          }
          back={stepBackButton}
        />
      );
    }

    if (prompt?.kind === "bowler") {
      return (
        <BowlerPrompt
          overNo={prompt.overNo}
          candidates={xi(bowlingTeam)}
          innings={innings}
          warningsFor={(playerId) =>
            bowlerWarnings(draft, state, innings.inningsNo, playerId, {
              kind: "new_over",
              overNo: prompt.overNo,
            })
          }
          styleOf={(playerId) => knownBowling(draft.setup, state, playerId)}
          back={stepBackButton}
          onPick={(playerId, style) => {
            // PRD §6.4 allows the override but wants a reason logged. The warnings the
            // scorer was shown *are* the reason he overrode, so they are what gets
            // recorded — a typed note is better and belongs with the corrections UI in
            // #15, but recording nothing would leave a deliberate decision looking like a
            // mis-tap six weeks later.
            const overridden = bowlerWarnings(draft, state, innings.inningsNo, playerId, {
              kind: "new_over",
              overNo: prompt.overNo,
            });
            const reason =
              overridden.length === 0
                ? null
                : `scorer proceeded despite: ${overridden.map((w) => w.message).join("; ")}`;
            advance((current) =>
              buildNewOver(current, innings.inningsNo, prompt.overNo, playerId, reason, style),
            );
          }}
        />
      );
    }

    if (sheet?.kind === "wagon" && at !== null) {
      const held = { runs: sheet.runs, latch: sheet.latch };
      const strikerHand = handOfBatter(at.strikerId);
      // The ground cannot be read without knowing which way he stands. Only a match scored before
      // the hand was recorded gets here: a batter who walks in now is asked at the walk-in. The
      // ball stays held while this is answered, and the wheel opens as soon as the answer lands.
      if (strikerHand === null) {
        return (
          <HandStep
            name={name(at.strikerId)}
            current={null}
            onPick={(next) => applyStyleChange({ kind: "hand", playerId: at.strikerId, hand: next }, sheet)}
            onBack={() => setSheet(null)}
          />
        );
      }
      return (
        <WagonWheelSheet
          runs={held.runs}
          batterName={name(at.strikerId)}
          hand={strikerHand}
          latchLabel={latchLabel(held.latch)}
          editing={false}
          initial={null}
          onHand={(next) => requestStyleChange({ kind: "hand", playerId: at.strikerId, hand: next }, sheet, sheet)}
          // Back keeps the latch: the tap that was wrong was the run digit, and a no-ball the
          // scorer correctly latched should not have to be tapped again.
          onBack={() => setSheet(null)}
          onPlace={(shot) => onWagonDone(held, shot)}
          onSkip={() => onWagonDone(held, null)}
        />
      );
    }

    if (sheet?.kind === "confirm_change") {
      const { change, keep, done } = sheet;
      if (change.kind === "hand") {
        const who = name(change.playerId);
        const now = handOfBatter(change.playerId);
        const impact = handChangeImpact(state, innings.inningsNo, change.playerId);
        const saved = squadMember(change.playerId);
        const own = saved?.styleSelfSet?.includes("battingHand") === true;
        const word = (hand: BatterHand): string => battingHandName(hand).toLowerCase();
        return (
          <ConfirmChange
            title={`Change ${who} to ${word(change.hand)}?`}
            lines={[
              `This is not just for the next ball. It applies to ${impact.balls === 1 ? "the 1 ball" : `all ${impact.balls} balls`} ${who} has faced this innings.`,
              impact.shots > 0
                ? `${who}'s ${impact.shots} wagon wheel ${impact.shots === 1 ? "shot" : "shots"} will be redrawn: shots shown on the off side will move to the leg side, and leg to off. The score will not change.`
                : "The score will not change.",
              own && saved?.battingHand !== undefined
                ? `This match only. ${who}'s profile says ${word(saved.battingHand)}.`
                : `${who} will also be ${word(change.hand)} in future matches.`,
            ]}
            keepLabel={now === null ? "Cancel" : `Keep ${word(now)}`}
            changeLabel={`Change to ${word(change.hand)}`}
            onKeep={() => setSheet(keep)}
            onChange={() => applyStyleChange(change, done)}
          />
        );
      }
      const who = name(change.bowlerId);
      const line = innings.bowling.find((entry) => entry.playerId === change.bowlerId);
      const armWord = change.arm === "left" ? "left-arm" : "right-arm";
      const saved = squadMember(change.bowlerId);
      const own = saved?.styleSelfSet?.includes("bowlingArm") === true;
      return (
        <ConfirmChange
          title={`Change ${who} to a ${armWord} bowler?`}
          lines={[
            `This is not just for the next over. It applies to every over ${who} has bowled this innings${line === undefined ? "" : ` (${formatOvers(line.overs)} overs)`}.`,
            `The scorecard will show ${who} as ${bowlingStyleName(change.arm ?? null, change.type ?? line?.type ?? null) ?? armWord}. The score will not change.`,
            own && saved?.bowlingArm !== undefined
              ? `This match only. ${who}'s profile says ${saved.bowlingArm}-arm.`
              : `${who} will also bowl ${armWord} in future matches.`,
          ]}
          keepLabel={`Keep ${line?.arm ?? "current"}-arm`}
          changeLabel={`Change to ${armWord}`}
          onKeep={() => setSheet(keep)}
          onChange={() => applyStyleChange(change, done)}
        />
      );
    }

    if (sheet?.kind === "wicket" && at !== null && state.header?.striker && state.header.nonStriker) {
      return (
        <WicketSheet
          latch={latch}
          freeHit={at.freeHit}
          ruleSet={draft.setup.ruleSet}
          striker={state.header.striker}
          nonStriker={state.header.nonStriker}
          bowlerId={at.bowlerId}
          keeperId={at.keeperId}
          fielders={xi(bowlingTeam)}
          recentFielderId={recentFielderId}
          initialRuns={sheet.runs}
          name={name}
          // Cancel un-latches `W` but leaves `WD`/`NB` alone. Keeping the wicket latched
          // would trap the scorer: the next run tap would reopen the sheet he just backed
          // out of. Clearing the whole latch instead would throw away the no-ball he
          // correctly recorded before realising the wicket was not one.
          onCancel={() => {
            setSheet(null);
            setLatch((current) => ({ ...current, wicket: false }));
          }}
          onConfirm={onWicketConfirmed}
        />
      );
    }

    if (sheet?.kind === "retire" && at !== null) {
      return (
        <RetireSheet
          striker={at.strikerId}
          nonStriker={at.nonStrikerId}
          name={name}
          onRetire={(playerId, reason) =>
            advance((current) => buildRetired(current, innings.inningsNo, playerId, reason))
          }
          onClose={() => setSheet(null)}
        />
      );
    }

    if (sheet?.kind === "bowler_change") {
      return (
        <BowlerPrompt
          overNo={state.header?.overs.completed ?? 0}
          candidates={xi(bowlingTeam)}
          innings={innings}
          warningsFor={(playerId) =>
            bowlerWarnings(draft, state, innings.inningsNo, playerId, { kind: "mid_over" })
          }
          styleOf={(playerId) => knownBowling(draft.setup, state, playerId)}
          onPick={(playerId, style) =>
            advance((current) => buildBowlerChange(current, innings.inningsNo, playerId, null, style))
          }
          onClose={() => setSheet(null)}
        />
      );
    }

    if (sheet?.kind === "change_opener") {
      const { slot } = sheet;
      const cancel = { label: "Cancel", onClick: () => setSheet(null) };
      const header = state.header;
      if (slot === "bowler") {
        const now = header?.bowler?.playerId;
        return (
          <BowlerPrompt
            overNo={0}
            candidates={xi(bowlingTeam)}
            innings={innings}
            title="Change the opening bowler"
            subtitle={now == null ? "Tap to select" : `${name(now)} now · tap to select`}
            back={cancel}
            warningsFor={() => []}
            styleOf={(playerId) => knownBowling(draft.setup, state, playerId)}
            onPick={(playerId, style) =>
              advance((current) => changeOpener(current, derive(current), "bowler", playerId, { style }))
            }
          />
        );
      }
      const now = slot === "striker" ? header?.striker?.playerId : header?.nonStriker?.playerId;
      const other = slot === "striker" ? header?.nonStriker?.playerId : header?.striker?.playerId;
      const otherLabel = slot === "striker" ? "at the other end" : "on strike";
      return (
        <BatterPrompt
          takesStrike={slot === "striker"}
          // Anyone in the XI but the batter at the other end; the one in the slot now is marked.
          available={xi(battingTeam).filter((player) => player.id !== other)}
          innings={innings}
          title={slot === "striker" ? "Change the striker" : "Change the non-striker"}
          subtitle={`${now == null ? "" : `${name(now)} now · `}${other == null ? "" : `${name(other)} stays ${otherLabel}`}`}
          {...(now == null ? {} : { current: now })}
          back={cancel}
          handOf={(playerId) => knownBattingHand(draft.setup, state, playerId)}
          onPick={(playerId, hand) =>
            advance((current) => changeOpener(current, derive(current), slot, playerId, { hand }))
          }
        />
      );
    }

    if (sheet?.kind === "keeper_change") {
      const currentKeeperId = bowlingTeam.keeperId;
      return (
        <div style={{ padding: "var(--ds-space-3)" }}>
          <div style={{ fontWeight: 700, fontSize: "var(--ds-text-md)", marginBottom: "var(--ds-space-1)" }}>
            Who is keeping wicket?
          </div>
          <div className="ds-caption" style={{ marginBottom: "var(--ds-space-3)" }}>
            {currentKeeperId === null ? "No keeper marked" : `Current: ${name(currentKeeperId)}`}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "var(--ds-space-2)", maxHeight: "40dvh", overflowY: "auto" }}>
            {xi(bowlingTeam).map((player) => (
              <button
                key={player.id}
                className={`ds-key ds-key--sm${currentKeeperId === player.id ? " ds-key--primary" : ""}`}
                style={{ height: 48, fontSize: "var(--ds-text-sm)", fontFamily: "var(--ds-font-sans)" }}
                onClick={() => {
                  ledger.updateSetup((current) => ({
                    ...current,
                    teams: current.teams.map((t) =>
                      t.id === innings.bowlingTeamId ? { ...t, keeperId: player.id } : t,
                    ) as [typeof current.teams[0], typeof current.teams[1]],
                  }));
                  setSheet(null);
                }}
              >
                {player.name}
              </button>
            ))}
          </div>
          <button className="ds-btn ds-btn--secondary ds-btn--block" style={{ marginTop: "var(--ds-space-3)" }} onClick={() => setSheet(null)}>Cancel</button>
        </div>
      );
    }

    return (
      <Pad
        latch={latch}
        ruleSet={draft.setup.ruleSet}
        freeHit={state.header?.freeHit ?? false}
        wagon={wagon}
        onWagon={toggleWagon}
        canUndo={canUndoLast(draft, state)}
        top={openersStrip}
        canSwap={canSwapIncomingBatters(draft, state)}
        onSwap={() => advance((current) => swapIncomingBatters(current, derive(current)))}
        onLatch={(key: LatchKey) => {
          // The first touch of a multi-tap ball, and the moment the entry span opens.
          loop.touched();
          setLatch((current) => toggleLatch(current, key));
        }}
        onClearLatch={() => setLatch(NO_LATCH)}
        onRuns={onRuns}
        // The three that came out of the `…more` sheet (#42). Each opens the panel it
        // always opened; what changed is that reaching it is one tap rather than two.
        onRetire={() => setSheet({ kind: "retire" })}
        onChangeBowler={() => setSheet({ kind: "bowler_change" })}
        onChangeKeeper={() => setSheet({ kind: "keeper_change" })}
        onLedger={() => setSheet({ kind: "ledger" })}
        undoableBalls={undoableBalls(draft)}
        onUndo={() => {
          loop.corrected("undo");
          advance((current) => undoLast(current, derive(current)));
        }}
        onUndoMany={(count) => {
          // Counted per ball undone, not per gesture: scrubbing back four balls is four
          // mis-recorded balls, and calling it one correction would flatter the pad.
          for (let i = 0; i < count; i++) loop.corrected("undo");
          advance((current) => undoLastBalls(current, count));
        }}
      />
    );
  })();

  return (
    <div className="app-screen">
      <div className="app-screen__read" style={{ padding: "0 var(--ds-space-4)" }}>
        <div style={{ paddingTop: 48, paddingBottom: "var(--ds-space-2)" }}>
          <div className="row" style={{ marginBottom: "var(--ds-space-3)" }}>
            <button className="ds-iconbtn ds-iconbtn--sm" aria-label="Back" onClick={onExit}>
              <svg viewBox="0 0 24 24"><path d="m14 6-6 6 6 6" /></svg>
            </button>
            <span style={{ flex: 1, minWidth: 0 }}>
              <b style={{ display: "block", fontSize: "var(--ds-text-md)", letterSpacing: "var(--ds-tracking-tight)" }}>
                {battingTeam.name} v {bowlingTeam.name}
              </b>
              <span className="ds-caption">
                {draft.setup.ruleSet.name} &middot;{" "}
                {state.header ? formatOvers(state.header.overs) : "0.0"}{" "}
                {innings.oversAvailable === null ? "" : `/ ${innings.oversAvailable} `}ov
              </span>
            </span>
            <SyncPill state={ledger.sync} {...(onSignIn === undefined ? {} : { onSignIn })} />
            <span className="ds-badge ds-badge--success" style={{ flex: "none" }}>
              <i className="ds-dot" />Live
            </span>
          </div>

          <ForeignBanner
            state={ledger.sync}
            onDiscard={() => {
              if (
                window.confirm(
                  "Discard the balls another account scored? They will not be sent, and this cannot be undone.",
                )
              ) {
                void ledger.discardForeign();
              }
            }}
          />
          <GapBanner gaps={ledger.gaps} />
          <StuckBanner stuck={ledger.stuck} />
          <Header
            state={state}
            innings={innings}
            name={name}
            battingTeamName={battingTeam.name}
            format={draft.setup.ruleSet.name}
          />
          <OverStrip
            balls={state.header?.currentOver ?? []}
            overNo={state.header?.overs.completed ?? 0}
            onTapBall={(eventId) => setSheet({ kind: "edit_ball", eventId })}
            onOpenLedger={() => setSheet({ kind: "ledger" })}
          />
        </div>
        <Scorecard state={state} name={name} teamName={teamName} />
        <div style={{ height: "var(--ds-space-5)" }} />
      </div>

      <div className="app-screen__tap">{bottom}</div>
    </div>
  );
}
